import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, ListTodo, X, Check, Save, Filter } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tip } from "@/components/Tip";
import {
  fetchProjectTasks,
  createProjectTask,
  updateProjectTask,
  deleteProjectTask,
  TASK_STATUSES,
  TASK_PRIORITIES,
  projectNotFoundMessage,
  isProjectNotFound,
  type Task,
  type TaskStatus,
  type TaskPriority,
} from "@/lib/api/projects";

interface Props {
  projectId: string;
  projectLabel: string;
}

const STATUS_LABELS: Record<TaskStatus, string> = {
  todo: "К выполнению",
  in_progress: "В работе",
  done: "Готово",
  archived: "Архив",
};

const STATUS_COLORS: Record<TaskStatus, string> = {
  todo: "bg-slate-500/15 text-slate-400",
  in_progress: "bg-sky-500/15 text-sky-400",
  done: "bg-emerald-500/15 text-emerald-400",
  archived: "bg-slate-500/15 text-tertiary",
};

const PRIORITY_LABELS: Record<TaskPriority, string> = {
  low: "Низкий",
  normal: "Обычный",
  high: "Высокий",
};

const PRIORITY_COLORS: Record<TaskPriority, string> = {
  low: "text-tertiary",
  normal: "text-sky-400",
  high: "text-rose-400",
};

const EMPTY_FORM = {
  title: "",
  description: "",
  status: "todo" as TaskStatus,
  priority: "normal" as TaskPriority,
};

export function ProjectTasks({ projectId, projectLabel }: Props) {
  const [items, setItems] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<TaskStatus | "">("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setItems(await fetchProjectTasks(projectId, statusFilter || undefined));
    } catch (e) {
      setError(isProjectNotFound(e) ? projectNotFoundMessage() : "Не удалось загрузить задачи проекта");
    } finally {
      setLoading(false);
    }
  }, [projectId, statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
  };

  const startCreate = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(true);
  };

  const startEdit = (t: Task) => {
    setForm({
      title: t.title,
      description: t.description || "",
      status: t.status,
      priority: t.priority,
    });
    setEditingId(t.id);
    setShowForm(true);
  };

  const save = async () => {
    const title = form.title.trim();
    if (!title || saving) return;
    const payload = {
      title,
      description: form.description.trim() || null,
      status: form.status,
      priority: form.priority,
    };
    setSaving(true);
    try {
      if (editingId) {
        await updateProjectTask(projectId, editingId, payload);
      } else {
        await createProjectTask(projectId, payload);
      }
      resetForm();
      await load();
    } catch (e) {
      setError(isProjectNotFound(e) ? projectNotFoundMessage() : "Не удалось сохранить задачу");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    setConfirmDelete(null);
    try {
      await deleteProjectTask(projectId, id);
      await load();
    } catch (e) {
      setError(isProjectNotFound(e) ? projectNotFoundMessage() : "Не удалось удалить задачу");
    }
  };

  // Quick status cycle on the row (todo → in_progress → done → archived → todo).
  const cycleStatus = async (t: Task) => {
    const order: TaskStatus[] = ["todo", "in_progress", "done", "archived"];
    const next = order[(order.indexOf(t.status) + 1) % order.length];
    try {
      await updateProjectTask(projectId, t.id, { status: next });
      await load();
    } catch (e) {
      setError(isProjectNotFound(e) ? projectNotFoundMessage() : "Не удалось изменить статус");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-sm font-medium text-primary">Задачи проекта</h2>
          <p className="text-xs text-tertiary mt-0.5">
            Список дел проекта «{projectLabel}»: статус, приоритет и описание.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Filter size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-tertiary pointer-events-none" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as TaskStatus | "")}
              className="h-9 pl-8 pr-3 rounded-lg bg-hoverbg border border-hairline text-sm text-primary focus:outline-none focus:border-accent"
              aria-label="Фильтр по статусу"
            >
              <option value="">Все статусы</option>
              {TASK_STATUSES.map((s) => (
                <option key={s} value={s}>{STATUS_LABELS[s]}</option>
              ))}
            </select>
          </div>
          {!showForm && (
            <button
              onClick={startCreate}
              className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-sm font-medium bg-accent/15 text-accent hover:bg-accent/25 transition-colors"
            >
              <Plus size={15} />
              Добавить
            </button>
          )}
        </div>
      </div>

      {/* Create / Edit form */}
      {showForm && (
        <div className="rounded-xl border border-hairline bg-surface p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-secondary">
              {editingId ? "Редактирование задачи" : "Новая задача"}
            </span>
            <button onClick={resetForm} className="text-tertiary hover:text-primary" aria-label="Закрыть">
              <X size={16} />
            </button>
          </div>
          <div>
            <label className="block text-xs text-secondary mb-1.5" htmlFor="task-title">Название</label>
            <input
              id="task-title"
              value={form.title}
              maxLength={500}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Например: реализовать endpoint задач"
              className="w-full h-9 px-3 rounded-lg bg-hoverbg border border-hairline text-sm text-primary focus:outline-none focus:border-accent"
            />
          </div>
          <div>
            <label className="block text-xs text-secondary mb-1.5" htmlFor="task-desc">Описание (необязательно)</label>
            <textarea
              id="task-desc"
              value={form.description}
              rows={3}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Детали задачи"
              className="w-full px-3 py-2 rounded-lg bg-hoverbg border border-hairline text-sm text-primary focus:outline-none focus:border-accent resize-y"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-secondary mb-1.5" htmlFor="task-status">Статус</label>
              <select
                id="task-status"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value as TaskStatus })}
                className="w-full h-9 px-2 rounded-lg bg-hoverbg border border-hairline text-sm text-primary focus:outline-none focus:border-accent"
              >
                {TASK_STATUSES.map((s) => (
                  <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-secondary mb-1.5" htmlFor="task-priority">Приоритет</label>
              <select
                id="task-priority"
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value as TaskPriority })}
                className="w-full h-9 px-2 rounded-lg bg-hoverbg border border-hairline text-sm text-primary focus:outline-none focus:border-accent"
              >
                {TASK_PRIORITIES.map((p) => (
                  <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-3 pt-1">
            <button
              onClick={() => void save()}
              disabled={saving || !form.title.trim()}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-sm font-medium bg-accent/15 text-accent hover:bg-accent/25 transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              {editingId ? "Сохранить" : "Добавить"}
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-400">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="py-12 flex items-center justify-center">
          <Loader2 size={20} className="animate-spin text-tertiary" />
        </div>
      ) : !error && items.length === 0 ? (
        <div className="py-12 flex flex-col items-center gap-3 text-center">
          <ListTodo size={28} className="text-tertiary" />
          <p className="text-sm text-secondary">
            {statusFilter ? "Нет задач с выбранным статусом." : "Задач пока нет."}
          </p>
          <p className="text-xs text-tertiary max-w-xs">
            Создайте первую задачу — она появится в списке проектов «{projectLabel}».
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((t) => (
            <li key={t.id} className="rounded-xl border border-hairline bg-surface px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => void cycleStatus(t)}
                      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && void cycleStatus(t)}
                      title="Нажмите, чтобы сменить статус"
                      className={cn(
                        "inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-medium cursor-pointer hover:opacity-80 transition-opacity",
                        STATUS_COLORS[t.status] ?? "bg-slate-500/15 text-slate-400",
                      )}
                    >
                      {STATUS_LABELS[t.status] ?? t.status}
                    </span>
                    <span className={cn("text-[11px] font-medium", PRIORITY_COLORS[t.priority] ?? "text-tertiary")}>
                      {PRIORITY_LABELS[t.priority] ?? t.priority}
                    </span>
                    <span
                      className={cn(
                        "text-sm font-medium text-primary truncate",
                        t.status === "done" && "line-through opacity-60",
                        t.status === "archived" && "opacity-50",
                      )}
                    >
                      {t.title}
                    </span>
                  </div>
                  {t.description && (
                    <p className="mt-1.5 text-sm text-secondary whitespace-pre-wrap break-words">{t.description}</p>
                  )}
                  <div className="mt-1.5 flex items-center gap-2 text-[11px] text-tertiary">
                    <span>{new Date(t.created_at).toLocaleDateString("ru-RU")}</span>
                    {t.status === "done" && t.completed_at && (
                      <span>· завершена {new Date(t.completed_at).toLocaleDateString("ru-RU")}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Tip label="Редактировать">
                    <button
                      onClick={() => startEdit(t)}
                      className="w-7 h-7 rounded-md flex items-center justify-center text-secondary hover:text-primary hover:bg-hoverbg transition-colors"
                      aria-label="Редактировать"
                    >
                      <Pencil size={14} />
                    </button>
                  </Tip>
                  <Tip label="Удалить">
                    <button
                      onClick={() => setConfirmDelete(t.id)}
                      className="w-7 h-7 rounded-md flex items-center justify-center text-secondary hover:text-rose-400 hover:bg-hoverbg transition-colors"
                      aria-label="Удалить"
                    >
                      <Trash2 size={14} />
                    </button>
                  </Tip>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Delete confirm */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div role="dialog" className="w-full max-w-sm rounded-2xl border border-hairline bg-surface p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Save size={18} className="text-rose-400" />
              <h3 className="text-sm font-medium text-primary">Удалить задачу?</h3>
            </div>
            <p className="text-sm text-secondary">
              Задача будет удалена из проекта. Действие нельзя отменить.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className="h-9 px-3.5 rounded-lg text-sm text-secondary hover:text-primary hover:bg-hoverbg transition-colors"
              >
                Отмена
              </button>
              <button
                onClick={() => void remove(confirmDelete)}
                className="h-9 px-3.5 rounded-lg text-sm font-medium bg-rose-500/15 text-rose-400 hover:bg-rose-500/25 transition-colors"
              >
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
